import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import {
	map,
	filter,
	withLatestFrom,
	concatMap,
	mapTo,
	tap,
} from 'rxjs/operators';
import { DataPersistence } from '@nrwl/angular';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { UserClient, User } from 'src/shared/api';
import { AdminUsersState, AdminUsersPartialState } from './admin-user.reducer';
import { AdminUserListComponent } from '../admin-user-list/admin-user-list.component';
import { getAllListArgs } from 'src/shared/constants';
import * as AdminUserSelectors from './admin-user.selectors';
import * as AdminUsersActions from './admin-user.actions';
import { AdminUserDetailComponent } from '../admin-user-detail/admin-user.component';
import { grabGuidFromParams } from 'src/shared/utilities/utility-functions';
import { Store, select } from '@ngrx/store';
import { RailProsError } from 'src/shared/models/rail-pros-error.model';
import { usersFragments } from '../../users.routes';
import { notifyAfterSaving } from 'src/shared/utilities/custom-rxjs-operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
	convertToAdminUserListArgs,
	convertToAdminUserListParams,
} from '../admin-user-list/admin-user-list.utility';

@Injectable()
export class AdminUserEffects {
	updateListQueryParams$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(AdminUsersActions.filtersUpdated),
				map(({ filters }) => convertToAdminUserListParams(filters)),
				tap((queryParams) => {
					this.router.navigate(usersFragments.adminUserList, {
						queryParams,
					});
				})
			),
		{ dispatch: false }
	);

	getAdminUsers$ = createEffect(() =>
		this.dataPersistence.navigation(AdminUserListComponent, {
			run: (r: ActivatedRouteSnapshot, _state: AdminUsersState) => {
				let listArgs = convertToAdminUserListArgs(r.queryParamMap);

				// right now we only support search text on the back-end
				if (!listArgs.searchText) {
					listArgs = getAllListArgs;
				}

				return this.userClient.getAdminUsers(listArgs).pipe(
					map((pagedResults) => pagedResults.results),
					map((users) =>
						AdminUsersActions.adminUsersLoaded({
							users,
						})
					)
				);
			},
			onError: (_a: ActivatedRouteSnapshot, error: any) => {
				throw new Error(error);
			},
		})
	);

	selectUser$ = createEffect(() =>
		this.dataPersistence.navigation(AdminUserDetailComponent, {
			run: (r: ActivatedRouteSnapshot, _state: AdminUsersState) => {
				const selectedAdminUserId = grabGuidFromParams(r.paramMap);

				return AdminUsersActions.adminUserSelected({
					selectedAdminUserId,
				});
			},
			onError: (_a: ActivatedRouteSnapshot, error: any) => {
				throw new Error(error);
			},
		})
	);

	loadUserFromStore$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AdminUsersActions.adminUserSelected),
			map((action) => action.selectedAdminUserId),
			filter(Boolean),
			withLatestFrom(
				this.store.pipe(
					select(AdminUserSelectors.getSelectedAdminUser)
				),
				(_id, user) => user
			),
			filter(Boolean),
			map((user) => AdminUsersActions.adminUserLoadedFromStore({ user }))
		)
	);

	loadUserFromApi$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AdminUsersActions.adminUserSelected),
			map((action) => action.selectedAdminUserId),
			filter<string>(Boolean),
			withLatestFrom(
				this.store.pipe(select(AdminUserSelectors.getSelectedAdminUser))
			),
			filter(([_id, user]) => !user),
			concatMap(([id, _user]) => this.userClient.getAdminUser(id)),
			map((user) =>
				AdminUsersActions.adminUserLoadedFromApi({
					user,
				})
			)
		)
	);

	saveUser$ = createEffect(() =>
		this.dataPersistence.pessimisticUpdate(
			AdminUsersActions.saveRequested,
			{
				run: (
					action: ReturnType<typeof AdminUsersActions.saveRequested>,
					_state: AdminUsersState
				) => {
					const user = action.formValue as User;
					user.loginName = user.email;

					return this.userClient
						.putUser(user)
						.pipe(
							mapTo(AdminUsersActions.adminUserSaved({ user }))
						);
				},
				onError: (
					_action: ReturnType<typeof AdminUsersActions.saveRequested>,
					_error: any
				) => {
					throw new RailProsError(450, '', 'Error saving user');
				},
			}
		)
	);

	closeAfterSaving$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(AdminUsersActions.adminUserSaved),
				withLatestFrom(
					this.store.pipe(
						select(AdminUserSelectors.getCloseAfterSaving)
					),
					(_, shouldClose) => shouldClose
				),
				filter(Boolean),
				tap(() => this.router.navigate(usersFragments.adminUserList))
			),
		{ dispatch: false }
	);

	notifyAfterSaving$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(AdminUsersActions.adminUserSaved),
				notifyAfterSaving(this.snackbar)
			),
		{ dispatch: false }
	);

	constructor(
		private readonly actions$: Actions,
		private readonly router: Router,
		private readonly snackbar: MatSnackBar,
		private readonly dataPersistence: DataPersistence<AdminUsersState>,
		private readonly store: Store<AdminUsersPartialState>,
		private readonly userClient: UserClient
	) {}
}
